/**
 * Style samples, for picking a direction. Not part of the app.
 *
 * Everything here is deliberately self-contained: one file, one `<style>` block
 * with a prefix per sample, and its own images under `public/stijlen/`. Nothing
 * outside this page and that folder is touched, so whichever direction is
 * chosen — or none — the rest can be deleted in one go.
 *
 * Each sample renders the *same* four things, because comparing four home
 * screens says more than four mood boards: a header, the two buttons, a card
 * with its back, and a seat badge from the table.
 *
 * The images are `.webp`, which is not in the service worker's precache glob —
 * so these samples add nothing to the ten entries the app ships offline.
 */

import { Link } from 'react-router-dom';
import { THEME_LABELS, useSettings, type Theme } from '@/store/settings';
import arcadeBack from '@/kaartruggen/arcade.webp';
import kaartkamerBack from '@/kaartruggen/kaartkamer.webp';
import speelsBack from '@/kaartruggen/speels.webp';
import polyBack from '@/kaartruggen/poly.webp';
import pixelBack from '@/kaartruggen/pixel.webp';

/*
 * Imported rather than fetched from a fixed path: these are the same files the
 * themes print on a card back, and the bundler fingerprints them so editing one
 * actually reaches a device that has already cached it.
 */
const BACKS: Record<string, string | null> = {
  nu: null,
  arcade: arcadeBack,
  kaartkamer: kaartkamerBack,
  speels: speelsBack,
  poly: polyBack,
  pixel: pixelBack,
};

export default function Stijlen() {
  return (
    <div className="min-h-dvh bg-base-950 text-white">
      <style>{CSS}</style>

      <header className="mx-auto max-w-2xl px-5 pb-4 pt-10">
        <h1 className="text-3xl font-bold tracking-tight">Stijlen</h1>
        <p className="mt-1 text-slate-400">
          Vier richtingen, met steeds dezelfde onderdelen. Kijk er op je telefoon naar — dat is waar
          het spel gespeeld wordt.
        </p>
        <Link
          to="/"
          className="mt-3 inline-block text-sm text-accent-400 underline underline-offset-4"
        >
          terug naar de app
        </Link>
        <p className="mt-3 text-sm text-slate-500">
          Onder elk voorbeeld staat een knop om het meteen aan te zetten. Het is ook te wisselen
          onderaan het startscherm, en het onthoudt zich per toestel.
        </p>
      </header>

      <div className="mx-auto max-w-2xl space-y-8 px-4 pb-16">
        <Sample
          theme="nu"
          id="nu"
          name="1 · Zoals het nu is"
          note="Donkere tafel, groen accent. Rustig, en niets leidt af van de kaarten. De maatstaf om de rest tegen af te zetten."
        />
        <Sample
          theme="arcade"
          id="arcade"
          name="2 · Arcade"
          note="Neon op zwart, scanlijnen over het scherm, blokletters. Het meest uitgesproken van de vier — leuk voor de kinderen, en na een uur spelen misschien vermoeiend."
        />
        <Sample
          theme="kaartkamer"
          id="kaartkamer"
          name="3 · Kaartkamer"
          note="Groen laken, goud filigrein, een serif. Klassiek en volwassen; past bij klaverjassen zoals het vroeger op tafel lag."
        />
        <Sample
          theme="speels"
          id="speels"
          name="4 · Speels"
          note="Vlakke kleuren, dikke lijnen. Ditzelfde is de stijl van de getekende dieren die er al in zitten, dus dit is de enige richting waarin de avatars en de rest hetzelfde spel spelen."
        />
        <Sample
          theme="poly"
          id="poly"
          name="5 · Poly"
          note="Facetten en scherpe hoeken — nergens een ronding. Koeler en strakker dan de rest; leest als een modern bordspel eerder dan als een kaartspel."
        />
        <Sample
          theme="pixel"
          id="pixel"
          name="6 · Pixel"
          note="8-bit, met een echt pixellettertype (Press Start 2P, 6,7 kB). Dat lettertype is groot voor zijn puntgrootte, dus alles staat wat ruimer — kijk vooral of lange namen nog passen."
        />
      </div>
    </div>
  );
}

function Sample({
  id,
  theme,
  name,
  note,
}: {
  id: string;
  theme: Theme;
  name: string;
  note: string;
}) {
  const active = useSettings((state) => state.theme);
  const setTheme = useSettings((state) => state.setTheme);

  return (
    <section>
      <h2 className="mb-1 text-lg font-semibold">{name}</h2>
      <p className="mb-3 text-sm leading-relaxed text-slate-400">{note}</p>

      <div className={`st st-${id}`}>
        <div className="st-screen">
          <header className="st-head">
            <div>
              <p className="st-title">Kaarten</p>
              <p className="st-sub">Drie spellen, aan tafel of op de telefoon</p>
            </div>
          </header>

          <div className="st-card">
            <div className="st-cardhead">
              <p className="st-game">Boerenbridge</p>
              <span className="st-rules">regels</span>
            </div>
            <p className="st-blurb">Voorspel je slagen en maak ze waar</p>
            <div className="st-buttons">
              <span className="st-btn st-btn-primary">Score bijhouden</span>
              <span className="st-btn st-btn-secondary">Live spelen</span>
            </div>
          </div>

          <div className="st-table">
            <div className="st-seat">
              <span className="st-avatar" aria-hidden>
                🦊
              </span>
              <span className="st-name">Mila</span>
              <span className="st-score">2/3</span>
            </div>

            <div className="st-cards">
              <span className="st-play st-red">
                A<i>♥</i>
              </span>
              <span className="st-play">
                10<i>♠</i>
              </span>
              {BACKS[id] ? (
                <img className="st-back" src={BACKS[id]!} alt="" />
              ) : (
                <span className="st-back st-back-css" aria-hidden />
              )}
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setTheme(theme)}
        disabled={active === theme}
        className="mt-2 w-full rounded-xl border border-white/10 bg-base-800/60 px-4 py-3 text-sm hover:bg-base-700/60 disabled:opacity-50"
      >
        {active === theme
          ? `${THEME_LABELS[theme].name} staat aan`
          : `Zet ${THEME_LABELS[theme].name} aan`}
      </button>
    </section>
  );
}

/*
 * One block, prefixed per sample. Not Tailwind, because these are throwaway and
 * mixing four conflicting design systems into the utility classes would leave
 * traces everywhere once a choice is made.
 */
const CSS = `
.st { border-radius: 20px; overflow: hidden; }
.st-screen { padding: 18px; display: flex; flex-direction: column; gap: 16px; position: relative; }
.st-head { display: flex; align-items: flex-start; justify-content: space-between; }
.st-title { font-size: 26px; font-weight: 800; letter-spacing: -0.02em; margin: 0; }
.st-sub { font-size: 13px; margin: 4px 0 0; opacity: 0.7; }
.st-card { border-radius: 16px; padding: 14px; }
.st-cardhead { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.st-game { font-size: 17px; font-weight: 700; margin: 0; }
.st-rules { font-size: 12px; opacity: 0.55; text-decoration: underline; text-underline-offset: 3px; }
.st-blurb { font-size: 13px; margin: 2px 0 12px; opacity: 0.7; }
.st-buttons { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.st-btn { display: flex; align-items: center; justify-content: center; min-height: 44px;
          border-radius: 12px; font-size: 14px; font-weight: 600; }
.st-table { border-radius: 16px; padding: 14px; display: flex; align-items: center;
            justify-content: space-between; gap: 12px; }
.st-seat { display: flex; align-items: center; gap: 8px; }
.st-avatar { width: 34px; height: 34px; border-radius: 999px; display: inline-flex;
             align-items: center; justify-content: center; font-size: 19px; }
.st-name { font-size: 14px; font-weight: 600; }
.st-score { font-size: 13px; opacity: 0.8; font-variant-numeric: tabular-nums; }
.st-cards { display: flex; gap: 6px; }
.st-play { width: 42px; height: 60px; border-radius: 7px; display: flex; flex-direction: column;
           align-items: center; justify-content: center; font-size: 15px; font-weight: 700; line-height: 1; }
.st-play i { font-style: normal; font-size: 15px; }
.st-back { width: 42px; height: 60px; border-radius: 7px; object-fit: cover; display: block; }

/* ---- 1 · zoals het nu is ------------------------------------------------ */
.st-nu { background: #0b1220; border: 1px solid rgba(255,255,255,0.06); }
.st-nu .st-title { color: #fff; }
.st-nu .st-sub, .st-nu .st-blurb { color: #94a3b8; }
.st-nu .st-card { border: 1px solid rgba(34,197,94,0.3); background: rgba(34,197,94,0.05); }
.st-nu .st-btn-primary { background: #22c55e; color: #070d18; }
.st-nu .st-btn-secondary { border: 1px solid rgba(34,197,94,0.4); color: #dcfce7; }
.st-nu .st-table { background: #131c2e; border: 1px solid rgba(255,255,255,0.06); }
.st-nu .st-avatar { background: #1d2942; }
.st-nu .st-play { background: #f8fafc; color: #0f172a; }
.st-nu .st-red { color: #dc2626; }
.st-nu .st-back-css { background:
  repeating-linear-gradient(45deg, #1e3a8a 0 6px, #1e40af 6px 12px);
  border: 2px solid #f8fafc; }

/* ---- 2 · arcade ---------------------------------------------------------- */
.st-arcade { background: #06010f; border: 1px solid #ff2fd0; }
.st-arcade .st-screen { font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; }
/* Scanlines and a vignette, both as pseudo-elements over the whole screen —
   the standard CRT recipe. pointer-events none so nothing swallows a tap. */
.st-arcade .st-screen::before { content: ""; position: absolute; inset: 0; pointer-events: none;
  background: repeating-linear-gradient(to bottom, rgba(0,0,0,0.28) 0 1px, transparent 1px 3px);
  z-index: 5; }
.st-arcade .st-screen::after { content: ""; position: absolute; inset: 0; pointer-events: none;
  background: radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.55) 100%); z-index: 6; }
.st-arcade .st-title { color: #00f0ff; text-transform: uppercase; letter-spacing: 0.08em;
  text-shadow: 0 0 6px #00f0ff, 0 0 18px rgba(0,240,255,0.6); font-size: 22px; }
.st-arcade .st-sub { color: #ff2fd0; text-transform: uppercase; font-size: 11px; letter-spacing: 0.1em; }
.st-arcade .st-card { background: rgba(255,47,208,0.07); border: 1px solid #ff2fd0;
  box-shadow: 0 0 14px rgba(255,47,208,0.35) inset; }
.st-arcade .st-game { color: #ffe600; text-transform: uppercase; letter-spacing: 0.06em;
  text-shadow: 0 0 8px rgba(255,230,0,0.7); }
.st-arcade .st-rules, .st-arcade .st-blurb { color: #8be9ff; }
.st-arcade .st-btn { text-transform: uppercase; letter-spacing: 0.06em; font-size: 12px; border-radius: 4px; }
.st-arcade .st-btn-primary { background: #ffe600; color: #06010f; box-shadow: 0 3px 0 #b8a600; }
.st-arcade .st-btn-secondary { border: 2px solid #00f0ff; color: #00f0ff; box-shadow: 0 0 10px rgba(0,240,255,0.4); }
.st-arcade .st-table { background: #0a0320; border: 1px solid #00f0ff;
  background-image: linear-gradient(rgba(0,240,255,0.12) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(0,240,255,0.12) 1px, transparent 1px);
  background-size: 16px 16px; }
.st-arcade .st-avatar { background: #1a0433; border: 1px solid #ff2fd0; }
.st-arcade .st-name { color: #00f0ff; }
.st-arcade .st-score { color: #ffe600; }
.st-arcade .st-play { background: #0f0524; color: #00f0ff; border: 1px solid #00f0ff; border-radius: 3px;
  box-shadow: 0 0 8px rgba(0,240,255,0.4); }
.st-arcade .st-red { color: #ff2fd0; border-color: #ff2fd0; box-shadow: 0 0 8px rgba(255,47,208,0.4); }
.st-arcade .st-back { border-radius: 3px; }

/* ---- 3 · kaartkamer ------------------------------------------------------ */
.st-kaartkamer { background: #10261a; border: 1px solid rgba(212,175,55,0.35); }
.st-kaartkamer .st-screen { font-family: Georgia, "Times New Roman", serif; }
.st-kaartkamer .st-title { color: #f5ecd7; font-weight: 700; letter-spacing: 0.01em; }
.st-kaartkamer .st-sub { color: #c8bb93; font-style: italic; }
.st-kaartkamer .st-card { background: #16351f; border: 1px solid rgba(212,175,55,0.4);
  box-shadow: inset 0 0 0 3px rgba(212,175,55,0.12); }
.st-kaartkamer .st-game { color: #d4af37; }
.st-kaartkamer .st-rules, .st-kaartkamer .st-blurb { color: #c8bb93; }
.st-kaartkamer .st-btn-primary { background: linear-gradient(#e3c463, #c19a2b); color: #10261a; }
.st-kaartkamer .st-btn-secondary { border: 1px solid rgba(212,175,55,0.55); color: #e3c463; }
/* Felt: two very faint diagonal gradients, so it reads as cloth without a bitmap. */
.st-kaartkamer .st-table { background:
  repeating-linear-gradient(45deg, rgba(0,0,0,0.05) 0 2px, transparent 2px 4px),
  repeating-linear-gradient(-45deg, rgba(255,255,255,0.03) 0 2px, transparent 2px 4px), #14301c;
  border: 1px solid rgba(212,175,55,0.3); }
.st-kaartkamer .st-avatar { background: #1d4429; border: 1px solid rgba(212,175,55,0.4); }
.st-kaartkamer .st-name { color: #f5ecd7; }
.st-kaartkamer .st-score { color: #d4af37; }
.st-kaartkamer .st-play { background: #fdfaf1; color: #1a1a1a; font-family: Georgia, serif; }
.st-kaartkamer .st-red { color: #a8202a; }

/* ---- 5 · poly ------------------------------------------------------------ */
.st-poly { background: #12182b; border: 1px solid rgba(94,234,212,0.3); border-radius: 0; }
.st-poly .st-card, .st-poly .st-table, .st-poly .st-btn, .st-poly .st-play,
.st-poly .st-back { border-radius: 0; }
.st-poly .st-title { color: #e2f6f2; }
.st-poly .st-sub, .st-poly .st-blurb, .st-poly .st-rules { color: #93b0b8; }
.st-poly .st-card { background: #1b243e; border: 1px solid rgba(94,234,212,0.28); }
.st-poly .st-game { color: #5eead4; }
.st-poly .st-btn-primary { background: #2dd4bf; color: #0a0e1a; }
.st-poly .st-btn-secondary { border: 1px solid rgba(94,234,212,0.5); color: #5eead4; }
.st-poly .st-table { background:
  linear-gradient(135deg, #1b243e 0 50%, #202b48 50% 100%); border: 1px solid rgba(94,234,212,0.22); }
.st-poly .st-avatar { background: #26344e; border-radius: 0; }
.st-poly .st-name { color: #e2f6f2; }
.st-poly .st-score { color: #5eead4; }
.st-poly .st-play { background: #f2f8f7; color: #0a0e1a; }
.st-poly .st-red { color: #d1495b; }

/* ---- 6 · pixel ----------------------------------------------------------- */
.st-pixel { background: #1a1033; border: 3px solid #0c081a; border-radius: 0; }
.st-pixel .st-screen { font-family: 'Press Start 2P', ui-monospace, monospace;
  font-size: 10px; line-height: 1.9; }
.st-pixel img { image-rendering: pixelated; }
.st-pixel .st-title { color: #fdf6e3; font-size: 15px; line-height: 1.5; }
.st-pixel .st-sub, .st-pixel .st-blurb, .st-pixel .st-rules { color: #a99bd6; font-size: 8px; }
.st-pixel .st-card { background: #281a4a; border: 3px solid #0c081a; border-radius: 0; }
.st-pixel .st-game { color: #7ee787; font-size: 11px; }
.st-pixel .st-btn { border-radius: 0; border: 3px solid #0c081a; font-size: 8px; }
.st-pixel .st-btn-primary { background: #5ccc6a; color: #0c081a; box-shadow: 4px 4px 0 #0c081a; }
.st-pixel .st-btn-secondary { background: #3a2666; color: #fdf6e3; box-shadow: 4px 4px 0 #0c081a; }
.st-pixel .st-table { background: #281a4a; border: 3px solid #0c081a; border-radius: 0; }
.st-pixel .st-avatar { background: #fdf6e3; border: 3px solid #0c081a; border-radius: 0; }
.st-pixel .st-name { color: #fdf6e3; font-size: 9px; }
.st-pixel .st-score { color: #7ee787; font-size: 9px; }
.st-pixel .st-play { background: #fdf6e3; color: #0c081a; border: 3px solid #0c081a;
  border-radius: 0; font-size: 11px; }
.st-pixel .st-red { color: #d94f4f; }
.st-pixel .st-back { border: 3px solid #0c081a; border-radius: 0; }

/* ---- 4 · speels ---------------------------------------------------------- */
.st-speels { background: #16213e; border: 3px solid #0d1526; }
.st-speels .st-title { color: #fff8e7; }
.st-speels .st-sub { color: #a8b5d1; }
.st-speels .st-card { background: #1e2d4f; border: 3px solid #0d1526; border-radius: 18px; }
.st-speels .st-game { color: #fff8e7; }
.st-speels .st-rules, .st-speels .st-blurb { color: #a8b5d1; }
.st-speels .st-btn { border-radius: 14px; border: 3px solid #0d1526; }
.st-speels .st-btn-primary { background: #6ec96e; color: #0d1526; box-shadow: 0 3px 0 #0d1526; }
.st-speels .st-btn-secondary { background: #f4863f; color: #0d1526; box-shadow: 0 3px 0 #0d1526; }
.st-speels .st-table { background: #1e2d4f; border: 3px solid #0d1526; border-radius: 18px; }
.st-speels .st-avatar { background: #fff8e7; border: 3px solid #0d1526; }
.st-speels .st-name { color: #fff8e7; }
.st-speels .st-score { color: #6ec96e; font-weight: 700; }
.st-speels .st-play { background: #fff8e7; color: #0d1526; border: 3px solid #0d1526; border-radius: 8px; }
.st-speels .st-red { color: #f4863f; }
.st-speels .st-back { border: 3px solid #0d1526; border-radius: 8px; }
`;
